import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
export default class datetimeHelper {
  static formats = {
    default: { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' },
    short: { year: 'numeric', month: 'numeric', day: 'numeric' },
    long: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
  };

  static shortDateFormat = 'M/D/YYYY';

  static datetimeNoDayFormat = 'MMM D, YYYY LT';

  static defaultDateTimeFormat = 'M/D/YYYY h:mmA';

  static monthDayYearTimeFormat = 'MMM D, YYYY, h:mm A';

  static getFormat = (format: string): any => {
    let result: any = this.formats.default;
    Object.entries(this.formats).forEach(([k, v]) => {
      if (k === format) result = v;
    });
    return result;
  };

  static formatDate = (
    datetimeStr: string | undefined | null,
    options?: { format?: string; missingDisplay?: string }
  ) => {
    const defaultOptions = {
      format: 'default',
      missingDisplay: '',
    };
    const { format, missingDisplay } = {
      ...defaultOptions,
      ...options,
    };
    if (!datetimeStr) return missingDisplay;

    if (isNaN(Date.parse(datetimeStr))) return missingDisplay;
    const dt = datetimeStr.includes('Z') ? dayjs(datetimeStr) : dayjs.utc(datetimeStr);

    return dt.format(this.shortDateFormat);
  };

  static formatDateTime = (datetimeStr: string | undefined, options?: { format?: string; missingDisplay?: string }) => {
    const defaultOptions = {
      format: 'default',
      missingDisplay: '',
    };
    const { format, missingDisplay } = {
      ...defaultOptions,
      ...options,
    };
    if (!datetimeStr) return missingDisplay;

    if (isNaN(Date.parse(datetimeStr))) return missingDisplay;

    const dt = datetimeStr.includes('Z') ? dayjs(datetimeStr) : dayjs.utc(datetimeStr);

    if (format === 'monthDayYearTime') {
      return dt.format(this.monthDayYearTimeFormat);
    }

    return dt.format(datetimeStr.length > 10 ? this.defaultDateTimeFormat : this.shortDateFormat);
  };

  static timestamp = () => {
    const ts = new Date();
    return `${ts.toLocaleDateString()} - ${ts.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  };

  static normalizeDateForComparison = (dateValue: string | null | undefined): string => {
    if (!dateValue || dateValue === '') {
      return '';
    }
    const date = dayjs(dateValue);
    if (!date.isValid()) {
      return '';
    }
    return date.format('YYYY-MM-DD');
  };

  /**
   * Validate date string format (YYYY-MM-DD)
   * @param dateString - Date string to validate
   * @returns true if the date string is valid and in YYYY-MM-DD format, false otherwise
   */
  static isValidDateString = (dateString: string): boolean => {
    if (!dateString) return false;
    const date = dayjs(dateString, 'YYYY-MM-DD', true);
    return date.isValid();
  };

  /** LexID user birthdate: strict MM-DD-YYYY for form and API payloads. */
  static birthdateFormFormat = 'MM-DD-YYYY';

  static isValidBirthdateString = (dateString: string): boolean => {
    if (!dateString) return false;
    return dayjs(dateString, this.birthdateFormFormat, true).isValid();
  };

  /** MM-DD-YYYY for form state from API ISO / date-only / already MM-DD-YYYY. */
  static apiValueToBirthdateFormString = (iso: string | undefined | null): string => {
    if (!iso) return '';
    const trimmed = String(iso).trim();
    if (dayjs(trimmed, this.birthdateFormFormat, true).isValid()) {
      return dayjs(trimmed, this.birthdateFormFormat, true).format(this.birthdateFormFormat);
    }
    const utc = dayjs.utc(trimmed);
    if (!utc.isValid()) return '';
    return utc.format(this.birthdateFormFormat);
  };

  /** List/detail: MM-DD-YYYY from API or stored birthdate string. */
  static formatBirthdateDisplay = (value: string | undefined | null, missingDisplay = ''): string => {
    if (!value) return missingDisplay;
    const formatted = this.apiValueToBirthdateFormString(value);
    return formatted || missingDisplay;
  };

  /** Parse MM-DD-YYYY or API ISO into Dayjs for DatePicker. */
  static birthdateToDayjs = (value: string | null | undefined): Dayjs | null => {
    if (value == null || String(value).trim() === '') return null;
    const trimmed = String(value).trim();
    const fromForm = dayjs(trimmed, this.birthdateFormFormat, true);
    if (fromForm.isValid()) return fromForm;
    const utc = dayjs.utc(trimmed);
    if (!utc.isValid()) return null;
    const ymd = utc.format('YYYY-MM-DD');
    const local = dayjs(ymd, 'YYYY-MM-DD', true);
    return local.isValid() ? local : null;
  };

  /** MM-DD-YYYY for API from DatePicker. */
  static dayjsToBirthdateFormString = (d: Dayjs | null): string => {
    if (!d || !d.isValid()) return '';
    return d.format(this.birthdateFormFormat);
  };
}
